import { formatTimezoneList } from "common";
import { isNil } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { Arrival, Experience, Timeslot } from "types";
import { useDebounce } from "use-debounce";
import { useSeller } from "../../hooks/useAccount";
import { hasTimeScheduled, isCheckInAllowed, isVirtualExperience } from "../../models/Experience";
import { CreatePurchaseItemState, CreatePurchaseState, useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { fetchCityOptions } from "../Common/CitySelect";
import { ArrivalPicker } from "../DatePicker/ArrivalPicker";
import { TimezonePicker } from "../Input/TimezonePicker";
import { AutoCheckIn } from "./AutoCheckIn";

interface CreatePurchaseArrivalPickerProps {
    value: CreatePurchaseItemState;
    experience: Experience;
}

export const CreatePurchasePackageArrivalPicker = ({ value: item, experience }: CreatePurchaseArrivalPickerProps) => {
    const seller = useSeller();

    const [search, setSearch] = useState("");
    const [debouncedSearch] = useDebounce(search, 500);

    const [timezone, setTimezone] = useState(item.arrivalTimezoneName);
    const handleTimezoneInputChange = (value: string) => setSearch(value);
    const handleTimezoneChange = (timezone?: string) => setTimezone(timezone);

    const activeItemIndex = useCreatePurchaseStore((state) => state.activeItemIndex);

    const { data: options, isLoading } = useQuery(
        ["cities", debouncedSearch],
        async () => fetchCityOptions(debouncedSearch),
        { enabled: search.length > 2 },
    );

    const { arrivalDate } = item;
    const arrival: Arrival = useMemo(() => {
        // This is memoized so there are fewer re-renders of the Arrival picker
        return {
            date: arrivalDate,
            time: item.arrivalTime ?? null,
            dateTime: item.arrivalDateTime,
        };
    }, [item.arrivalTime, item.arrivalDateTime, arrivalDate]);

    const errors = useCreatePurchaseStore((state) => state.errors);
    const arrivalErrors = useMemo(() => {
        return errors?.filter((error) => error.field === "arrivalDateTime") ?? [];
    }, [errors]);

    const handleChangeArrival = useCallback(
        (newArrival: Arrival, timeslot?: Timeslot) => {
            const { setPurchase } = useCreatePurchaseStore.getState().actions;

            setPurchase((draft) => {
                const itemIndex = getPackageItemIndex(draft, experience.id, activeItemIndex);
                if (draft.items[itemIndex]) {
                    draft.items[itemIndex].arrivalDate = newArrival.date;
                    if (isNil(newArrival.time)) {
                        draft.items[itemIndex].arrivalTime = undefined;
                    } else {
                        draft.items[itemIndex].arrivalTime = newArrival.time;
                    }

                    draft.items[itemIndex].timeslot = timeslot ?? null;
                }

                if (timezone) {
                    if (isNil(timeslot?.time)) {
                        draft.items[itemIndex].arrivalTime = undefined;
                    } else {
                        draft.items[itemIndex].arrivalTime = timeslot!.time;
                        draft.items[itemIndex].arrivalTimezoneName = timezone;
                    }
                }
            });
        },
        [activeItemIndex, experience.id, timezone],
    );

    return (
        <div className="flex flex-wrap justify-between">
            <div className="mr-4 flex-grow space-y-2">
                {isVirtualExperience(experience) && (
                    <TimezonePicker
                        className="w-[440px]"
                        options={formatTimezoneList(options ?? [])}
                        isLoading={isLoading}
                        value={timezone}
                        onTimezoneChange={handleTimezoneChange}
                        onInputChange={handleTimezoneInputChange}
                    />
                )}
                <ArrivalPicker
                    // Prevent the date picker from jumping on small screen sizes
                    key={`${experience.id}_${item.id}`}
                    placement={window.innerWidth < 1090 ? "bottom" : "right"}
                    showAddNewTimeSlot={hasTimeScheduled(experience)}
                    item={item}
                    value={arrival}
                    timezone={timezone}
                    privacyPref={{ privacy: item.privacy, allowedPrivacies: [item.privacy] }}
                    quantity={item.quantity}
                    product={item.product.id}
                    packageId={item.parentProduct?.id}
                    arrivalErrors={arrivalErrors}
                    onChange={handleChangeArrival}
                />
            </div>
            <div className="shrink-0 pt-8">{isCheckInAllowed(seller, experience) && <AutoCheckIn />}</div>
        </div>
    );
};

const getPackageItemIndex = (draft: CreatePurchaseState, experienceId: ID, activeItemIndex: number) => {
    const index = draft.items.findIndex((item) => item.product.id === experienceId);
    return index < 0 ? activeItemIndex : index;
};
